/**
 * Store locator — carte + liste des revendeurs (Leaflet / OSM)
 */
(function () {
  const EARTH_KM = 6371;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return EARTH_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(km, units, labels) {
    if (km == null || Number.isNaN(km)) return '';
    if (units === 'mi') {
      const mi = km * 0.621371;
      return mi < 10 ? `${mi.toFixed(1)} ${labels.mi}` : `${Math.round(mi)} ${labels.mi}`;
    }
    return km < 10 ? `${km.toFixed(1)} ${labels.km}` : `${Math.round(km)} ${labels.km}`;
  }

  function normalize(str) {
    return (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  function countryLabel(loc) {
    if (typeof loc.country === 'string') return loc.country;
    const map = loc.country || {};
    return map.fr || map.en || map.de || '';
  }

  function addressLines(loc) {
    const parts = [];
    if (loc.address1) parts.push(loc.address1);
    if (loc.address2) parts.push(loc.address2);
    const cityLine = [loc.postal, loc.city].filter(Boolean).join(' ');
    if (cityLine) parts.push(cityLine);
    const c = countryLabel(loc);
    if (c) parts.push(c);
    return parts;
  }

  function sameId(a, b) {
    return String(a) === String(b);
  }

  function directionsUrl(loc) {
    return `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`;
  }

  function pinIcon(active) {
    const fill = active ? '#3e2e2c' : '#3e2e2c';
    const svg = encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 36" fill="none">
        <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.268 21.732 0 14 0z" fill="${fill}"/>
        <circle cx="14" cy="14" r="5.5" fill="#faf8f6"/>
      </svg>`
    );
    return L.divIcon({
      className: `store-locator-marker${active ? ' is-active' : ''}`,
      html: `<span class="store-locator-marker__pin"><img src="data:image/svg+xml,${svg}" alt="" width="28" height="36"></span>`,
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      popupAnchor: [0, -32],
    });
  }

  function loadLeaflet() {
    return new Promise((resolve, reject) => {
      if (window.L) {
        resolve(window.L);
        return;
      }
      const cssId = 'store-locator-leaflet-css';
      if (!document.getElementById(cssId)) {
        const link = document.createElement('link');
        link.id = cssId;
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        link.crossOrigin = '';
        document.head.appendChild(link);
      }
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = () => resolve(window.L);
      script.onerror = () => reject(new Error('Leaflet failed to load'));
      document.head.appendChild(script);
    });
  }

  class StoreLocator {
    constructor(root) {
      this.root = root;
      this.locale = (root.dataset.locale || 'fr').slice(0, 2);
      this.units = root.dataset.units || 'km';
      this.partnerUrl = root.dataset.partnerUrl || '';
      this.labels = {
        searchPlaceholder: root.dataset.labelSearch || '',
        nearMe: root.dataset.labelNear || '',
        allCountries: root.dataset.labelAll || '',
        directions: root.dataset.labelDirections || '',
        website: root.dataset.labelWebsite || '',
        empty: root.dataset.labelEmpty || '',
        results: root.dataset.labelResults || '',
        loading: root.dataset.labelLoading || '',
        km: root.dataset.labelKm || 'km',
        mi: root.dataset.labelMi || 'mi',
        geoError: root.dataset.labelGeoError || '',
      };

      this.listEl = root.querySelector('[data-sl-list]');
      this.emptyEl = root.querySelector('[data-sl-empty]');
      this.filtersEl = root.querySelector('[data-sl-filters]');
      this.searchInput = root.querySelector('[data-sl-search]');
      this.clearBtn = root.querySelector('[data-sl-clear]');
      this.nearBtn = root.querySelector('[data-sl-near]');
      this.countEl = root.querySelector('[data-sl-count]');
      this.mapEl = root.querySelector('[data-sl-map]');
      this.dataEl = root.querySelector('[data-sl-locations]');

      this.locations = [];
      this.filtered = [];
      this.activeCountry = 'all';
      this.query = '';
      this.userCoords = null;
      this.activeId = null;
      this.markers = new Map();
      this.map = null;
    }

    readLocations() {
      if (!this.dataEl) return [];
      try {
        const raw = JSON.parse(this.dataEl.textContent.trim() || '[]');
        return Array.isArray(raw)
          ? raw.filter((l) => l && l.name && l.lat != null && l.lng != null)
          : [];
      } catch (err) {
        console.error('[store-locator] invalid locations JSON', err);
        return [];
      }
    }

    async init() {
      try {
        await loadLeaflet();
        this.locations = this.readLocations();
        this.buildFilters();
        this.initMap();
        this.bind();
        this.applyFilters({ fit: true });
        this.root.classList.add('is-ready');
      } catch (err) {
        console.error('[store-locator]', err);
        if (this.emptyEl) {
          this.emptyEl.hidden = false;
          this.emptyEl.textContent = this.labels.empty;
        }
      }
    }

    buildFilters() {
      const counts = new Map();
      this.locations.forEach((loc) => {
        const code = loc.country_code || 'XX';
        counts.set(code, (counts.get(code) || 0) + 1);
      });

      const order = ['FR', 'BE', 'CH', 'DE', 'ES', 'NL', 'PL', 'CZ', 'LT', 'EE', 'GR', 'RE', 'CA'];
      const codes = [...counts.keys()].sort((a, b) => {
        const ia = order.indexOf(a);
        const ib = order.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });

      const frag = document.createDocumentFragment();
      const all = document.createElement('button');
      all.type = 'button';
      all.className = 'store-locator__chip is-active';
      all.dataset.country = 'all';
      all.textContent = this.labels.allCountries;
      frag.appendChild(all);

      codes.forEach((code) => {
        const sample = this.locations.find((l) => l.country_code === code);
        const label = sample ? countryLabel(sample) : code;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'store-locator__chip';
        btn.dataset.country = code;
        btn.textContent = `${label} (${counts.get(code)})`;
        frag.appendChild(btn);
      });

      this.filtersEl.replaceChildren(frag);
    }

    initMap() {
      this.map = L.map(this.mapEl, {
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
      }).setView([46.6, 2.5], 5);

      const cartoKey = (this.root.dataset.cartoKey || '').trim();
      const cartoQs = cartoKey ? `?key=${encodeURIComponent(cartoKey)}` : '';
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png${cartoQs}`, {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(this.map);

      // Enable scroll zoom after interaction (desktop)
      this.map.on('focus', () => this.map.scrollWheelZoom.enable());
      this.map.on('blur', () => this.map.scrollWheelZoom.disable());
      this.mapEl.addEventListener('mouseenter', () => this.map.scrollWheelZoom.enable());
      this.mapEl.addEventListener('mouseleave', () => this.map.scrollWheelZoom.disable());
    }

    bind() {
      this.searchInput?.addEventListener('input', () => {
        this.query = this.searchInput.value;
        this.clearBtn?.classList.toggle('is-visible', Boolean(this.query));
        this.applyFilters({ fit: false });
      });

      this.clearBtn?.addEventListener('click', () => {
        this.searchInput.value = '';
        this.query = '';
        this.clearBtn.classList.remove('is-visible');
        this.searchInput.focus();
        this.applyFilters({ fit: true });
      });

      this.filtersEl?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-country]');
        if (!btn) return;
        this.activeCountry = btn.dataset.country;
        this.filtersEl.querySelectorAll('.store-locator__chip').forEach((c) => {
          c.classList.toggle('is-active', c === btn);
        });
        this.applyFilters({ fit: true });
      });

      this.nearBtn?.addEventListener('click', () => this.geolocate());

      this.listEl?.addEventListener('click', (e) => {
        const item = e.target.closest('[data-id]');
        if (!item) return;
        if (e.target.closest('a')) return;
        this.selectLocation(item.dataset.id, { pan: true, openPopup: true });
      });

      window.addEventListener(
        'resize',
        () => {
          this.map?.invalidateSize();
        },
        { passive: true }
      );
    }

    geolocate() {
      if (!navigator.geolocation) {
        alert(this.labels.geoError);
        return;
      }
      this.nearBtn.setAttribute('aria-busy', 'true');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.userCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this.nearBtn.removeAttribute('aria-busy');
          this.applyFilters({ fit: true, preferUser: true });
        },
        () => {
          this.nearBtn.removeAttribute('aria-busy');
          alert(this.labels.geoError);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    }

    matchesQuery(loc) {
      const q = normalize(this.query);
      if (!q) return true;
      const hay = normalize(
        [
          loc.name,
          loc.address1,
          loc.address2,
          loc.postal,
          loc.city,
          loc.region,
          countryLabel(loc),
          loc.country_code,
        ]
          .filter(Boolean)
          .join(' ')
      );
      return hay.includes(q);
    }

    applyFilters({ fit = false, preferUser = false } = {}) {
      let list = this.locations.filter((loc) => {
        if (this.activeCountry !== 'all' && loc.country_code !== this.activeCountry) return false;
        return this.matchesQuery(loc);
      });

      if (this.userCoords) {
        list = list
          .map((loc) => ({
            ...loc,
            _distance: haversineKm(this.userCoords.lat, this.userCoords.lng, loc.lat, loc.lng),
          }))
          .sort((a, b) => a._distance - b._distance);
      } else {
        list = list.slice().sort((a, b) => {
          const ca = countryLabel(a);
          const cb = countryLabel(b);
          const cityA = a.city || '';
          const cityB = b.city || '';
          return (
            (b.priority || 0) - (a.priority || 0) ||
            ca.localeCompare(cb, this.locale) ||
            cityA.localeCompare(cityB, this.locale) ||
            a.name.localeCompare(b.name, this.locale)
          );
        });
      }

      this.filtered = list;
      this.renderList();
      this.renderMarkers({ fit, preferUser });
      this.updateCount();
    }

    updateCount() {
      if (!this.countEl) return;
      const n = this.filtered.length;
      const tpl = this.labels.results || '{count}';
      this.countEl.textContent = tpl.replace('{count}', String(n));
    }

    renderList() {
      if (!this.listEl) return;
      if (!this.filtered.length) {
        this.listEl.replaceChildren();
        if (this.emptyEl) this.emptyEl.hidden = false;
        return;
      }
      if (this.emptyEl) this.emptyEl.hidden = true;

      const frag = document.createDocumentFragment();
      this.filtered.forEach((loc) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'store-locator__item' + (sameId(loc.id, this.activeId) ? ' is-active' : '');
        btn.dataset.id = String(loc.id);
        btn.setAttribute('aria-pressed', sameId(loc.id, this.activeId) ? 'true' : 'false');

        const name = document.createElement('span');
        name.className = 'store-locator__item-name';
        name.textContent = loc.name;

        const meta = document.createElement('span');
        meta.className = 'store-locator__item-meta';
        meta.textContent = [loc.postal, loc.city, countryLabel(loc)].filter(Boolean).join(' · ');

        btn.append(name, meta);

        if (loc._distance != null) {
          const dist = document.createElement('span');
          dist.className = 'store-locator__item-distance';
          dist.textContent = formatDistance(loc._distance, this.units, this.labels);
          btn.appendChild(dist);
        }

        const actions = document.createElement('div');
        actions.className = 'store-locator__item-actions';

        const dir = document.createElement('a');
        dir.className = 'store-locator__link';
        dir.href = directionsUrl(loc);
        dir.target = '_blank';
        dir.rel = 'noopener noreferrer';
        dir.textContent = this.labels.directions;
        actions.appendChild(dir);

        if (loc.website) {
          const web = document.createElement('a');
          web.className = 'store-locator__link';
          web.href = loc.website;
          web.target = '_blank';
          web.rel = 'noopener noreferrer';
          web.textContent = this.labels.website;
          actions.appendChild(web);
        }

        btn.appendChild(actions);
        li.appendChild(btn);
        frag.appendChild(li);
      });
      this.listEl.replaceChildren(frag);
    }

    renderMarkers({ fit = false, preferUser = false } = {}) {
      // Clear old
      this.markers.forEach((m) => m.remove());
      this.markers.clear();

      const bounds = [];
      this.filtered.forEach((loc) => {
        const marker = L.marker([loc.lat, loc.lng], {
          icon: pinIcon(sameId(loc.id, this.activeId)),
          title: loc.name,
        });
        const addr = addressLines(loc)
          .map((l) => `<div>${escapeHtml(l)}</div>`)
          .join('');
        const links = [
          `<a href="${directionsUrl(loc)}" target="_blank" rel="noopener noreferrer">${escapeHtml(this.labels.directions)}</a>`,
        ];
        if (loc.website) {
          links.push(
            `<a href="${escapeAttr(loc.website)}" target="_blank" rel="noopener noreferrer">${escapeHtml(this.labels.website)}</a>`
          );
        }
        marker.bindPopup(
          `<div class="store-locator-popup">
            <div class="store-locator-popup__name">${escapeHtml(loc.name)}</div>
            <div class="store-locator-popup__addr">${addr}</div>
            <div class="store-locator-popup__links">${links.join('')}</div>
          </div>`
        );
        marker.on('click', () => this.selectLocation(loc.id, { pan: false, openPopup: false, fromMap: true }));
        marker.addTo(this.map);
        this.markers.set(String(loc.id), marker);
        bounds.push([loc.lat, loc.lng]);
      });

      if (fit && bounds.length) {
        if (preferUser && this.userCoords) {
          this.map.setView([this.userCoords.lat, this.userCoords.lng], 10, { animate: true });
        } else if (bounds.length === 1) {
          this.map.setView(bounds[0], 13, { animate: true });
        } else {
          this.map.fitBounds(bounds, { padding: [36, 36], maxZoom: 12 });
        }
      }

      requestAnimationFrame(() => this.map.invalidateSize());
    }

    selectLocation(id, { pan = true, openPopup = true } = {}) {
      const key = String(id);
      this.activeId = key;
      this.renderList();

      this.markers.forEach((marker, mid) => {
        marker.setIcon(pinIcon(sameId(mid, key)));
      });

      const marker = this.markers.get(key);
      const loc =
        this.filtered.find((l) => sameId(l.id, key)) || this.locations.find((l) => sameId(l.id, key));
      if (marker && loc) {
        if (pan) this.map.panTo([loc.lat, loc.lng], { animate: true });
        if (openPopup) marker.openPopup();
      }

      const el = this.listEl?.querySelector(`[data-id="${CSS.escape(key)}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/'/g, '&#39;');
  }

  ready(() => {
    document.querySelectorAll('[data-store-locator]').forEach((root) => {
      const locator = new StoreLocator(root);
      locator.init();
    });
  });
})();
